import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faGraduationCap,
  faLayerGroup,
  faServer,
  faWandMagicSparkles,
  faEarthAsia,
} from '@fortawesome/free-solid-svg-icons';

import { careerYearsFormatted } from '../../utils/yearsOfExperience';
import CountUp from '../ui/CountUp';
import Reveal from '../ui/Reveal';
import SectionHeading from '../ui/SectionHeading';
import SpotlightCard from '../ui/SpotlightCard';
import LocalTime from './LocalTime';

import '../../styles/About/About.scss';

const EDUCATION = [
  {
    degree: "Master's in Software Engineering",
    school: 'Muhammad Ali Jinnah University',
    url: 'https://jinnah.edu/',
    period: '2020 – 2022',
  },
  {
    degree: "Bachelor's in Software Engineering",
    school: 'Mehran University of Engineering & Technology',
    url: 'https://www.muet.edu.pk/',
    period: '2015 – 2018',
  },
];

const PILLARS = [
  {
    icon: faLayerGroup,
    title: 'Web & mobile frontends',
    text: 'React, Next.js, Angular and React Native apps: microfrontends, reusable components and fast, accessible UI.',
  },
  {
    icon: faServer,
    title: 'Node.js & real-time backends',
    text: 'Node.js and REST/GraphQL APIs, Kafka event streams, and integrations with aviation, payment and logistics systems.',
  },
  {
    icon: faWandMagicSparkles,
    title: 'AI & LLM engineering',
    text: 'OpenAI APIs in production, plus LangChain and LangGraph agents for procurement and baggage-matching use cases.',
  },
];

const About = () => {
  const years = parseFloat(careerYearsFormatted());

  return (
    <section className="section about" id="about" aria-labelledby="about-title">
      <div className="container">
        <SectionHeading
          index="01"
          eyebrow="About me"
          id="about-title"
          title="Engineer by craft,"
          accent="builder at heart."
          description="I turn complex operational problems into fast, reliable products. Airport gates, delivery fleets, enterprise storefronts: software people depend on every day."
        />

        <div className="about__bento">
          <Reveal className="about__cell about__cell--intro" delay={0}>
            <SpotlightCard as={Link} to="/about/intro" className="about__card">
              <span className="about__num">01 / Story</span>
              <h3>
                Hello, I&apos;m <span className="gradient-text">Darshan Lal</span>.
              </h3>
              <p>
                A Dubai-based Software Engineer specialising in React, Angular, React Native, Next.js and Node.js. A fast
                learner who has shipped airport, logistics and e-commerce software, now adding LLM features to the mix.
              </p>
              <span className="about__more">
                Read my story <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </span>
            </SpotlightCard>
          </Reveal>

          <Reveal className="about__cell about__cell--years" delay={100}>
            <SpotlightCard as={Link} to="/about/experience" className="about__card about__card--center">
              <span className="about__num">02 / Experience</span>
              <p className="about__big">
                <CountUp end={years} decimals={1} />
                <span className="gradient-text">+</span>
              </p>
              <p>years shipping production software across aviation, e-commerce, logistics and SaaS.</p>
              <span className="about__more">
                Full timeline <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </span>
            </SpotlightCard>
          </Reveal>

          <Reveal className="about__cell about__cell--location" delay={200}>
            <SpotlightCard className="about__card about__card--location">
              <div className="about__globe" aria-hidden="true">
                <FontAwesomeIcon icon={faEarthAsia} />
              </div>
              <span className="about__num">Based in</span>
              <h3>Dubai, UAE</h3>
              <p className="about__clock">
                <span className="about__clock-dot" aria-hidden="true" /> Local time <LocalTime />
              </p>
              <p className="about__muted">
                Open to relocation. Roles: Frontend, Backend, Full Stack or Forward Deployed Engineer.
              </p>
            </SpotlightCard>
          </Reveal>

          <Reveal className="about__cell about__cell--pillars" delay={100}>
            <SpotlightCard className="about__card">
              <span className="about__num">03 / What I do</span>
              <ul className="about__pillars">
                {PILLARS.map((p) => (
                  <li key={p.title}>
                    <span className="about__pillar-icon" aria-hidden="true">
                      <FontAwesomeIcon icon={p.icon} />
                    </span>
                    <div>
                      <h4>{p.title}</h4>
                      <p>{p.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </SpotlightCard>
          </Reveal>

          <Reveal className="about__cell about__cell--edu" delay={200}>
            <SpotlightCard className="about__card">
              <span className="about__num">04 / Education</span>
              <ul className="about__edu">
                {EDUCATION.map((e) => (
                  <li key={e.school}>
                    <span className="about__edu-icon" aria-hidden="true">
                      <FontAwesomeIcon icon={faGraduationCap} />
                    </span>
                    <div>
                      <h4>{e.degree}</h4>
                      <a href={e.url} target="_blank" rel="noopener noreferrer">
                        {e.school}
                      </a>
                      <small>{e.period}</small>
                    </div>
                  </li>
                ))}
              </ul>
            </SpotlightCard>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
